/**
 * Drawing the answer onto the map, and rubbing out the previous one.
 *
 * React-free on purpose. Markers are not React's to own - they live on a Google
 * map object that React never rendered, and a component that tried to reconcile
 * them would be diffing a tree it cannot see. This module owns the whole set,
 * replaces it wholesale on every answer, and hands back a disposer.
 *
 * Two shapes, matching the server's two modes: a bubble carrying a count when
 * the viewport holds too many companies to draw, and a pin per company when it
 * does not.
 *
 * Styling lives in `index.css` under `crm-mapmark*` rather than in inline
 * styles here, for one reason that inline styles cannot do at all: a name that
 * appears on hover needs `:hover`, and a rule is the only way to write one.
 */
import type { MapCell, MapPoint } from "../../../../lib/companyMap";
import { WEB_NEED_MID } from "../../../../lib/webNeed";
import { currentPalette, type MapPalette } from "./mapTheme";

/**
 * The palette, as inline custom properties on one marker.
 *
 * ⚠ Set on the ELEMENT, never inherited from `:root`. These nodes are handed to
 * Google and rendered inside its own overlay subtree, where a property defined
 * on the document root does not resolve - it comes out as an empty string and
 * paints nothing. Declared here, on the element itself, it resolves normally and
 * the stylesheet stays readable. Every rule that reads one of these also carries
 * a literal fallback, so a marker still has a colour even if this ever stops
 * arriving.
 */
function paletteStyle(p: MapPalette, colour: string): string {
  return [
    `--mk: ${colour}`,
    `--mk-chip-bg: ${p.chipBg}`,
    `--mk-chip-fg: ${p.chipFg}`,
    `--mk-ring: ${p.ring}`,
    `--mk-edge: ${p.edge}`,
  ].join("; ");
}

/**
 * Whether a company is one of the ones somebody is looking for.
 *
 * The web need score answers this better than "has no website on file" ever
 * did - it already carries that fact as its largest single input, and it adds
 * whether the company can pay. But it is a stored column, so it is null until
 * the pass has run, and a map that turned grey because a job had not been
 * started would be a worse map than the one this replaced. Hence the fallback.
 */
function isLead(p: MapPoint): boolean {
  return p.webNeedScore == null ? !p.webUrl : p.webNeedScore >= WEB_NEED_MID;
}

/**
 * Above this many pins, names stop being drawn by default and appear on hover
 * instead.
 *
 * Not a performance limit - the labels are cheap. It is a legibility one:
 * beyond roughly this many, the names overlap into an unreadable mat and hide
 * the very dots they are labelling. Hover still reaches every one of them.
 */
const NAME_LIMIT = 150;

/** Any drawn set can be removed again. */
export interface DrawnLayer {
  clear: () => void;
}

/** Cluster bubbles grow with what they hold, but only just: area, not radius,
 *  is what the eye reads as quantity, and a linear radius makes a city look ten
 *  times bigger than a town it is only twice the size of. */
function bubbleSize(n: number): number {
  return Math.round(Math.min(60, 26 + Math.sqrt(n) * 1.6));
}

function el(html: string): HTMLElement {
  const box = document.createElement("div");
  box.innerHTML = html.trim();
  return box.firstElementChild as HTMLElement;
}

/** Escapes text going into the markup above. Company names are register data,
 *  not ours, and one with an angle bracket in it must not become an element. */
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
}

function short(n: number): string {
  if (n >= 1_000_000) return `${Math.round(n / 100_000) / 10}M`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return String(n);
}

/**
 * The cluster layer.
 *
 * Clicking a bubble zooms toward it rather than opening anything: the bubble is
 * not a thing, it is a count, and the only useful answer to "what is in here"
 * is a closer look. Two zoom levels at a time, because one feels broken and
 * three overshoots the town.
 */
export function drawCells(map: google.maps.Map, cells: MapCell[]): DrawnLayer {
  const palette = currentPalette();
  const markers = cells.map((c) => {
    const size = bubbleSize(c.n);
    // The brand indigo when most of the cell has no website on file. A cluster
    // is a mix, so the colour reports the majority rather than pretending to be
    // exact.
    const leadHeavy = c.noWeb * 2 >= c.n;
    const content = el(`
      <div class="crm-mapbubble${leadHeavy ? " is-lead" : ""}"
           style="--crm-bubble: ${size}px; ${paletteStyle(palette, leadHeavy ? palette.lead : palette.plain)}"
           role="button" tabindex="0">
        <span class="crm-mapbubble-n">${short(c.n)}</span>
        <span class="crm-mapbubble-tip">${esc(String(c.n))} ettevõtet${
          c.noWeb ? `, ${c.noWeb} ilma veebita` : ""
        }${c.avgNeed == null ? "" : `, keskmine veebivajadus ${c.avgNeed}`}</span>
      </div>`);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat: c.lat, lng: c.lng },
      content,
    });

    const zoomIn = () => {
      map.panTo({ lat: c.lat, lng: c.lng });
      map.setZoom(Math.min(20, (map.getZoom() ?? 7) + 2));
    };
    content.addEventListener("click", zoomIn);
    content.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter" || (e as KeyboardEvent).key === " ") zoomIn();
    });

    return marker;
  });

  return {
    clear: () => {
      for (const m of markers) m.map = null;
    },
  };
}

/**
 * Companies sharing one coordinate, as one entry per coordinate.
 *
 * ⚠ This is not clustering and it is not an optimisation. Companies are placed
 * by ADDRESS (see the geocoder), so every company registered at the same office
 * building gets the SAME coordinate down to the last decimal - the mirror holds
 * 346 662 companies at 182 081 distinct addresses. Drawn one pin per company,
 * their name labels land exactly on top of each other and the result is an
 * unreadable smear of overlapping text with no way to tell how many are under
 * it, which is what this replaces.
 *
 * The key is the raw coordinate pair, not a rounded one. These floats come from
 * a single ADS lookup written to every company at that address by one UPDATE,
 * so they are identical rather than merely close, and rounding would start
 * merging neighbouring buildings instead.
 *
 * Order is preserved, so the group's first member is the one the server ranked
 * highest - which is what the pin is named and coloured after.
 */
export function groupByPosition(points: MapPoint[]): MapPoint[][] {
  const groups = new Map<string, MapPoint[]>();
  for (const p of points) {
    const key = `${p.lat},${p.lng}`;
    const at = groups.get(key);
    if (at) at.push(p);
    else groups.set(key, [p]);
  }
  return [...groups.values()];
}

/**
 * The company layer.
 *
 * Every pin carries its name, not just a dot. Below `NAME_LIMIT` the names are
 * simply on - which is what makes the map readable without clicking anything,
 * and is the difference between "there are companies here" and "there is
 * Metsatoodete AS here". Above it they would overlap into noise, so they wait
 * for the pointer. The limit counts PINS rather than companies, because that is
 * what is competing for space on screen.
 *
 * Clicking calls back rather than opening a Google InfoWindow: the card belongs
 * in React beside the map, where it can carry the CRM's own buttons - open the
 * register record, ring the number, write to them. An InfoWindow could only
 * ever hold text.
 */
export function drawPoints(
  map: google.maps.Map,
  points: MapPoint[],
  onPick: (group: MapPoint[]) => void,
): DrawnLayer {
  const groups = groupByPosition(points);
  const named = groups.length <= NAME_LIMIT;
  const palette = currentPalette();
  let current: HTMLElement | null = null;

  const markers = groups.map((group) => {
    // The best prospect at the address speaks for it: the server ordered the
    // points by web need, so this is the company somebody would call first.
    const head = group[0] as MapPoint;
    const extra = group.length - 1;
    const lead = group.some(isLead);
    const label = extra > 0 ? `${head.name} +${extra}` : head.name;

    const content = el(`
      <div class="crm-mapmark${lead ? " is-lead" : ""}${named ? " is-named" : ""}${extra > 0 ? " is-many" : ""}"
           style="${paletteStyle(palette, lead ? palette.lead : palette.plain)}"
           role="button" tabindex="0" aria-label="${esc(label)}">
        <span class="crm-mapmark-dot">${extra > 0 ? `<span class="crm-mapmark-n">${group.length}</span>` : ""}</span>
        <span class="crm-mapmark-name">${esc(head.name)}${
          extra > 0 ? `<span class="crm-mapmark-more">+${extra}</span>` : ""
        }</span>
      </div>`);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: { lat: head.lat, lng: head.lng },
      content,
      // Amber pins ride above the rest. They are the ones somebody is looking
      // for, and at a dense address the lead must not end up under a dot that
      // already has a website.
      zIndex: lead ? 2 : 1,
    });

    const pick = () => {
      // The open pin stays marked while its card is up, so the operator can see
      // which of forty dots they are reading about.
      current?.classList.remove("is-on");
      content.classList.add("is-on");
      current = content;
      onPick(group);
    };

    content.addEventListener("click", pick);
    content.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Enter" || (e as KeyboardEvent).key === " ") {
        e.preventDefault();
        pick();
      }
    });

    return marker;
  });

  return {
    clear: () => {
      for (const m of markers) m.map = null;
    },
  };
}
